// PeptideVault v6.5 — Calendar + half-life

// ══════════════════════════════════════════════════════
// CALENDAR HELPERS
// ══════════════════════════════════════════════════════
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December']
const DAYS   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']

// Palette for peptide colors (cycles through)
// Okabe-Ito palette — optimized for color-vision deficiency (deuteranomaly/protanopia safe)
const CAL_COLORS = ['#0072B2','#E69F00','#009E73','#CC79A7','#56B4E9','#D55E00','#F0E442','#000000','#999999']
function peptideColor(name) {
  // Assign distinct palette colors by stable position in the sorted list of all peptide names,
  // so no two peptides collide (hashing could). Falls back to hash for names not in any list.
  const names = new Set()
  for (const inj of (S.injections||[])) if (inj.peptideName) names.add(inj.peptideName)
  for (const p of (S.peptides||[])) if (p.name) names.add(p.name)
  const sorted = [...names].sort()
  const idx = sorted.indexOf(name)
  if (idx >= 0) return CAL_COLORS[idx % CAL_COLORS.length]
  // Fallback for unknown names
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return CAL_COLORS[Math.abs(hash) % CAL_COLORS.length]
}

function fmt12(timeStr) {
  if (!timeStr) return ''
  // Handle formats: "8:00 AM", "08:00", "8:00:00"
  const ampm = timeStr.match(/(\d+):(\d+)(?::\d+)?\s*(AM|PM)?/i)
  if (!ampm) return timeStr
  let h = parseInt(ampm[1]), m = ampm[2]
  const period = ampm[3]
  if (period) return `${h}:${m} ${period.toUpperCase()}`
  // 24hr → 12hr
  const suf = h >= 12 ? 'PM' : 'AM'
  h = h % 12 || 12
  return `${h}:${m} ${suf}`
}

// Convert any injection time ("AM", "PM", "7:00 AM", "19:00") to minutes since midnight for consistent sorting
function injTimeMinutes(timeStr) {
  const t = String(timeStr||'').trim().toUpperCase()
  if (t === 'AM') return 7 * 60        // 07:00
  if (t === 'PM') return 19 * 60       // 19:00
  // 12-hour clock "7:00 AM" / "7:00 PM"
  let m = t.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/)
  if (m) {
    let h = parseInt(m[1]); const min = parseInt(m[2])
    if (m[3] === 'AM' && h === 12) h = 0
    if (m[3] === 'PM' && h !== 12) h += 12
    return h * 60 + min
  }
  // 24-hour "19:00"
  m = t.match(/^(\d{1,2}):(\d{2})$/)
  if (m) return parseInt(m[1]) * 60 + parseInt(m[2])
  return 7 * 60  // unknown → treat as morning
}

function injectionsForDate(dateStr) {
  return S.injections.filter(inj => inj.date === dateStr)
    .sort((a,b) => injTimeMinutes(a.time) - injTimeMinutes(b.time) || (a.peptideName||'').localeCompare(b.peptideName||''))
}

function injectionsForPeptide(peptideId, peptideName) {
  return S.injections.filter(inj =>
    inj.peptideId === peptideId || inj.peptideName === peptideName
  )
}

function dateKey(y, m, d) {
  return `${y}-${String(m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`
}

// ══════════════════════════════════════════════════════
// RENDER: CALENDAR TAB
// ══════════════════════════════════════════════════════
function renderCalendarTab() {
  const y = S.calYear, m = S.calMonth
  const today = new Date()
  const todayKey = dateKey(today.getFullYear(), today.getMonth(), today.getDate())

  // Build day cells
  const firstDay = new Date(y, m, 1).getDay()
  const daysInMonth = new Date(y, m+1, 0).getDate()
  const daysInPrev  = new Date(y, m, 0).getDate()

  // Filter injections by selected user (default: all)
  const userFilter = S.calUserFilter || 'all'
  const filteredInjections = S.injections.filter(inj =>
    userFilter === 'all' || (inj.userId || '') === userFilter ||
    (userFilter === 'unassigned' && !inj.userId))

  // All injections this month — build map, each day sorted AM→PM
  const injMap = {}
  for (const inj of filteredInjections) {
    if (!injMap[inj.date]) injMap[inj.date] = []
    injMap[inj.date].push(inj)
  }
  for (const k in injMap) {
    injMap[k].sort((a,b) => injTimeMinutes(a.time) - injTimeMinutes(b.time) || (a.peptideName||'').localeCompare(b.peptideName||''))
  }

  // P1-5: Build planned-events map from protocol engine
  const plannedMap = {}
  if (typeof resolveAllProtocols === 'function') {
    var allPlanned = resolveAllProtocols(S.protocols || [])
    for (var pei = 0; pei < allPlanned.length; pei++) {
      var pe = allPlanned[pei]
      if (!plannedMap[pe.date]) plannedMap[pe.date] = []
      plannedMap[pe.date].push(pe)
    }
  }

  let cells = ''
  // Previous month overflow
  for (let i = firstDay - 1; i >= 0; i--) {
    const d = daysInPrev - i
    const key = dateKey(y, m-1, d)
    cells += `<div class="cal-cell other-month" onclick="showDayDetail('${key}')">
      <div class="cal-date">${d}</div>
    </div>`
  }
  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    const key = dateKey(y, m, d)
    const dayInjs = injMap[key] || []
    const dayPlanned = plannedMap[key] || []
    const hasEvents = dayInjs.length > 0 || dayPlanned.length > 0
    const isToday = key === todayKey
    cells += `<div class="cal-cell ${hasEvents?'has-events':''} ${isToday?'today':''}" onclick="showDayDetail('${key}')">
      <div class="cal-date">${d}</div>
      <div class="cal-dots">
        ${dayInjs.slice(0,4).map(inj => {
          const u = (S.users||[]).find(x => x.id === inj.userId)
          const ring = (userFilter === 'all' && u) ? `box-shadow:0 0 0 1.5px ${u.color}` : ''
          return `<span class="cal-dot" style="background:${peptideColor(inj.peptideName)};${ring}" title="${esc(inj.peptideName)}${u?' · '+esc(u.name):''}"></span>`
        }).join('')}
        ${dayInjs.length > 4 ? `<span style="font-size:9px;color:var(--text4)">+${dayInjs.length-4}</span>` : ''}
      </div>
      ${dayInjs.slice(0,2).map(inj => {
        const u = (S.users||[]).find(x => x.id === inj.userId)
        const userPrefix = (userFilter === 'all' && u) ? `<span style="display:inline-block;width:6px;height:6px;border-radius:50%;background:${u.color};margin-right:3px;vertical-align:middle"></span>` : ''
        return `<span class="cal-event-pill" style="background:${peptideColor(inj.peptideName)}22;color:${peptideColor(inj.peptideName)}">${userPrefix}${fmt12(inj.time)} ${esc(inj.peptideName)}</span>`
      }).join('')}
      ${dayPlanned.slice(0,2).map(pe => {
        return `<span class="cal-event-pill" style="background:var(--accentL);color:var(--accent);font-style:italic;opacity:0.7">${esc((pe.peptideName||pe.protocolName||'').slice(0,12))} ${pe.doseAmount||''}${pe.doseUnit||''}</span>`
      }).join('')}
    </div>`
  }
  // Next month overflow
  const totalCells = firstDay + daysInMonth
  const remaining = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7)
  for (let d = 1; d <= remaining; d++) {
    const key = dateKey(y, m+1, d)
    cells += `<div class="cal-cell other-month" onclick="showDayDetail('${key}')">
      <div class="cal-date">${d}</div>
    </div>`
  }

  return `
  <div style="animation:fadeIn .25s ease">
    <div class="row" style="margin-bottom:18px;flex-wrap:wrap;gap:8px">
      <h2 style="font-size:20px;font-weight:700">Injection Calendar</h2>
      <div class="spacer"></div>
      <button class="btn btn-secondary btn-sm" onclick="importInjectionCSV()">${ICONS.plus} Import CSV</button>
      <button class="btn btn-secondary btn-sm" onclick="exportInjectionCSV()">${ICONS.export} Export CSV</button>
      <button class="btn btn-secondary btn-sm" onclick="openICSRangeModal()">${ICONS.calendar} Export ICS</button>
      <button class="btn btn-danger btn-sm" onclick="clearInjections()">${ICONS.trash} Clear All</button>
    </div>

    ${(S.users||[]).filter(u=>u.active).length ? `
    <div class="row" style="margin-bottom:14px;gap:6px;flex-wrap:wrap;align-items:center">
      <span style="font-size:12px;font-weight:600;color:var(--text3);margin-right:2px">Show:</span>
      <button class="btn btn-sm ${userFilter==='all'?'btn-primary':'btn-secondary'}" onclick="S.calUserFilter='all';render()">Everyone</button>
      ${(S.users||[]).filter(u=>u.active).map(u => `
        <button class="btn btn-sm ${userFilter===u.id?'btn-primary':'btn-secondary'}" onclick="S.calUserFilter='${u.id}';render()" style="${userFilter===u.id?'':`border-left:3px solid ${u.color}`}">
          <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${u.color};margin-right:5px;vertical-align:middle"></span>${esc(u.name)}
        </button>`).join('')}
      ${S.injections.some(i=>!i.userId) ? `
        <div class="spacer"></div>
        <button class="btn btn-ghost btn-sm" onclick="openModal('assignInjections')" title="Assign unassigned injections to a person">⚠ ${S.injections.filter(i=>!i.userId).length} unassigned — assign</button>
      ` : ''}
    </div>` : ''}

    <!-- Month navigator -->
    <div class="card" style="margin-bottom:16px">
      <div class="card-body" style="display:flex;align-items:center;justify-content:space-between;padding:12px 16px">
        <button class="btn btn-ghost" onclick="calPrev()">◀</button>
        <div style="text-align:center">
          <div style="font-size:18px;font-weight:700">${MONTHS[m]} ${y}</div>
          <div style="font-size:12px;color:var(--text3)">${filteredInjections.filter(i=>i.date.startsWith(`${y}-${String(m+1).padStart(2,'0')}`)).length} injections this month${userFilter!=='all'?` · ${esc((S.users||[]).find(u=>u.id===userFilter)?.name||'')}`:''}</div>
        </div>
        <button class="btn btn-ghost" onclick="calNext()">▶</button>
      </div>
    </div>

    <!-- Calendar grid -->
    <div class="card" style="margin-bottom:16px">
      <div class="card-body" style="padding:12px">
        <div class="cal-grid">
          ${DAYS.map(d => `<div class="cal-header-cell">${d}</div>`).join('')}
          ${cells}
        </div>
      </div>
    </div>

    <!-- Legend -->
    ${filteredInjections.length ? `
    <div class="card">
      <div class="card-header"><div class="card-title">Peptides</div></div>
      <div class="card-body" style="display:flex;flex-wrap:wrap;gap:10px">
        ${[...new Set(filteredInjections.map(i=>i.peptideName))].map(name => `
        <div class="row" style="gap:6px">
          <span class="cal-dot" style="background:${peptideColor(name)};width:10px;height:10px;margin-top:2px"></span>
          <span style="font-size:13px;font-weight:500">${esc(name)}</span>
        </div>`).join('')}
      </div>
    </div>` : `
    <div class="card">
      <div class="empty-state" style="padding:32px">
        <div class="empty-icon">📅</div>
        <p>No injections yet</p>
        <span>Import a CSV with columns: Date, Time, Peptide, Dosage, Draw Units</span>
      </div>
    </div>`}

    <!-- Day detail modal handled separately -->
    <div id="day-detail"></div>
  </div>`
}

// ── Calendar navigation ───────────────────────────────
function calPrev() {
  if (S.calMonth === 0) { S.calMonth = 11; S.calYear-- }
  else S.calMonth--
  render()
}
function calNext() {
  if (S.calMonth === 11) { S.calMonth = 0; S.calYear++ }
  else S.calMonth++
  render()
}

// ── Day detail panel ──────────────────────────────────
function showDayDetail(dateKey) {
  const injs = injectionsForDate(dateKey)
  if (!injs.length) return
  const [y, m, d] = dateKey.split('-')
  const label = new Date(+y, +m-1, +d).toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric', year:'numeric' })
  S.modal = 'dayDetail'
  S.modalData = { dateKey, label, injs }
  render()
}

// ── CSV Import ────────────────────────────────────────
async function importInjectionCSV() {
  if (!window.api) {
    // Browser fallback
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.csv'
    input.onchange = e => parseInjectionCSV(e.target.files[0])
    input.click()
    return
  }
  const fp = await window.api.pickFile({ title:'Import Injection CSV', filters:[{name:'CSV',extensions:['csv']}] })
  if (!fp) return
  const res = await window.api.readFile(fp)
  if (!res.ok) { showToast('Could not read file'); return }
  parseInjectionCSVText(res.data)
}

async function parseInjectionCSV(file) {
  const text = await file.text()
  parseInjectionCSVText(text)
}

function parseInjectionCSVText(text) {
  const lines = text.trim().split('\n')
  if (lines.length < 2) { showToast('CSV appears empty'); return }

  // Parse header — case insensitive, trim
  const header = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/[^a-z ]/g,''))
  const idx = {
    date:      header.findIndex(h => h.includes('date')),
    time:      header.findIndex(h => h.includes('time')),
    peptide:   header.findIndex(h => h.includes('peptide')),
    dosage:    header.findIndex(h => h.includes('dosage') || h.includes('dose')),
    drawUnits: header.findIndex(h => h.includes('draw') || h.includes('unit')),
    user:      header.findIndex(h => h.includes('user') || h.includes('person') || h.includes('taken')),
    notes:     header.findIndex(h => h.includes('note')),
  }

  // Resolve a user name/id string to a stored user id (defaults to first active user)
  const defaultUserId = (S.users||[]).find(u => u.active)?.id || S.users?.[0]?.id || ''
  const resolveUserId = (val) => {
    if (!val) return defaultUserId
    const v = val.trim().toLowerCase()
    const match = (S.users||[]).find(u =>
      u.id.toLowerCase() === v ||
      u.name.toLowerCase() === v ||
      (u.initials||'').toLowerCase() === v ||
      u.name.toLowerCase().startsWith(v))
    return match?.id || defaultUserId
  }

  let added = 0, skipped = 0
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim()
    if (!line) continue
    // Handle quoted CSV fields
    const cols = parseCSVLine(line)
    const rawDate = cols[idx.date]?.trim()
    const rawTime = cols[idx.time]?.trim() || ''
    const peptideName = cols[idx.peptide]?.trim() || ''
    const dosage    = cols[idx.dosage]?.trim() || ''
    const drawUnits = cols[idx.drawUnits]?.trim() || ''
    const userId    = idx.user >= 0 ? resolveUserId(cols[idx.user]) : defaultUserId

    if (!rawDate || !peptideName) { skipped++; continue }

    // Normalize date to YYYY-MM-DD
    const d = parseDate(rawDate)
    if (!d) { skipped++; continue }

    // Try to match to existing peptide
    const matched = S.peptides.find(p => p.name.toLowerCase() === peptideName.toLowerCase())

    S.injections.push({
      id: 'inj_' + uid(),
      date: d,
      time: rawTime,
      peptideName,
      peptideId: matched?.id || '',
      dosage,
      drawUnits,
      userId,
      notes: idx.notes >= 0 ? (cols[idx.notes]?.trim() || '') : ''
    })
    added++
  }

  save()
  render()
  showToast(`Imported ${added} injection${added!==1?'s':''}${skipped?' ('+skipped+' skipped)':''}`)
}

function parseDate(str) {
  // Handle M/D/YYYY, M/D/YY, MM/DD/YYYY, YYYY-MM-DD
  str = str.replace(/['"]/g, '').trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str
  const parts = str.split(/[\/\-]/)
  if (parts.length === 3) {
    let year = parts[2]
    if (year.length === 2) {
      // Two-digit year: 00-49 → 2000s, 50-99 → 1900s
      const yy = parseInt(year, 10)
      year = (yy < 50 ? '20' : '19') + year.padStart(2, '0')
    }
    if (year.length === 4) {
      return `${year}-${parts[0].padStart(2,'0')}-${parts[1].padStart(2,'0')}`
    }
  }
  return null
}

function parseCSVLine(line) {
  const cols = []
  let cur = '', inQuote = false
  for (let i = 0; i < line.length; i++) {
    const c = line[i]
    if (c === '"' && inQuote && line[i+1] === '"') { cur += '"'; i++ }
    else if (c === '"') { inQuote = !inQuote }
    else if (c === ',' && !inQuote) { cols.push(cur); cur = '' }
    else cur += c
  }
  cols.push(cur)
  return cols
}

// ── Peptide CSV Import ────────────────────────────────
function importPeptideCSV() {
  if (window.api) {
    importPeptideCSVDesktop()
  } else {
    const input = document.createElement('input')
    input.type = 'file'; input.accept = '.csv'
    input.onchange = e => { const f = e.target.files[0]; if (f) f.text().then(parsePeptideCSVText) }
    input.click()
  }
}
async function importPeptideCSVDesktop() {
  const fp = await window.api.pickFile({ title:'Import Peptide CSV', filters:[{name:'CSV',extensions:['csv']}] })
  if (!fp) return
  const res = await window.api.readFile(fp)
  if (res?.data) parsePeptideCSVText(res.data)
}
function parsePeptideCSVText(text) {
  const lines = text.trim().split(/\r?\n/)
  if (lines.length < 2) { showToast('CSV appears empty'); return }
  const header = parseCSVLine(lines[0]).map(h => h.trim().toLowerCase())
  const col = (...names) => header.findIndex(h => names.some(n => h.includes(n)))
  const idx = {
    name:   col('name'),
    vial:   col('vial'),
    cats:   col('categor'),
    unit:   col('unit'),
    thresh: col('threshold','low stock'),
    half:   col('half'),
    url:    col('pedia','url'),
    notes:  col('notes'),
  }
  if (idx.name < 0) { showToast('CSV must have a "Name" column'); return }
  let added = 0, skipped = 0
  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue
    const cols = parseCSVLine(lines[i])
    const name = cols[idx.name]?.trim()
    if (!name) { skipped++; continue }
    if (S.peptides.find(p => p.name.toLowerCase() === name.toLowerCase())) { skipped++; continue }
    // Parse categories: semicolon or pipe separated (commas are CSV delimiters)
    const catStr = idx.cats>=0 ? (cols[idx.cats]?.trim()||'') : ''
    const categories = catStr ? catStr.split(/[;|]/).map(c=>c.trim()).filter(Boolean) : []
    S.peptides.push({
      id: 'p_' + uid(),
      name,
      customVialName: idx.vial>=0 ? (cols[idx.vial]?.trim()||'') : '',
      categories,
      unit: idx.unit>=0 ? (cols[idx.unit]?.trim()||'vials') : 'vials',
      lowStockThreshold: idx.thresh>=0 ? +(cols[idx.thresh]?.trim()||0) : 0,
      halfLife: idx.half>=0 ? (cols[idx.half]?.trim()||'') : '',
      pepPediaUrl: idx.url>=0 ? (cols[idx.url]?.trim()||'') : '',
      absorptionRate: 0.8,
      isBlend: false, blendComponents: [],
      notes: idx.notes>=0 ? (cols[idx.notes]?.trim()||'') : '',
      protocolDocIds: [], skus: [],
      createdAt: now(), updatedAt: now()
    })
    added++
  }
  if (added) logChange('peptide', null, 'created', `Imported ${added} peptide(s) via CSV`)
  save(); render()
  showToast(`Imported ${added} peptide${added!==1?'s':''}${skipped?` (${skipped} skipped)`:''}`)
}

// ── Location CSV Import ───────────────────────────────
function importLocationCSV() {
  if (window.api) {
    importLocationCSVDesktop()
  } else {
    const input = document.createElement('input')
    input.type = 'file'; input.accept = '.csv'
    input.onchange = e => { const f = e.target.files[0]; if (f) f.text().then(parseLocationCSVText) }
    input.click()
  }
}
async function importLocationCSVDesktop() {
  const fp = await window.api.pickFile({ title:'Import Location CSV', filters:[{name:'CSV',extensions:['csv']}] })
  if (!fp) return
  const res = await window.api.readFile(fp)
  if (res?.data) parseLocationCSVText(res.data)
}
function parseLocationCSVText(text) {
  const lines = text.trim().split(/\r?\n/)
  if (lines.length < 2) { showToast('CSV appears empty'); return }
  const header = parseCSVLine(lines[0]).map(h => h.trim().toLowerCase())
  const nameIdx = header.findIndex(h => h.includes('name'))
  const capIdx = header.findIndex(h => h.includes('capacity') || h.includes('total'))
  const useFirst = nameIdx < 0  // if no "name" header, treat first column as the name
  let added = 0, updated = 0, skipped = 0
  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue
    const cols = parseCSVLine(lines[i])
    const name = (useFirst ? cols[0] : cols[nameIdx])?.trim()
    if (!name) { skipped++; continue }
    const capacity = capIdx >= 0 ? +(cols[capIdx]?.trim() || 0) : 0
    const existing = S.storageLocations.find(l => l.name.toLowerCase() === name.toLowerCase())
    if (existing) {
      // Update capacity on an existing location rather than skipping outright
      if (capacity > 0 && existing.totalCapacity !== capacity) { existing.totalCapacity = capacity; updated++ }
      else skipped++
      continue
    }
    S.storageLocations.push({ id: 'loc_' + uid(), name, totalCapacity: capacity })
    added++
  }
  if (added || updated) logChange('location', null, 'created', `Imported ${added} location(s)${updated?`, updated ${updated}`:''} via CSV`)
  save(); render()
  showToast(`Imported ${added} location${added!==1?'s':''}${updated?`, updated ${updated}`:''}${skipped?` (${skipped} skipped)`:''}`)
}

// ── Supply Location Inventory CSV Import ──────────────
// Flat locations are supply-only; peptide placement uses vaultAssignments.
function importStorageInventoryCSV() {
  if (window.api) {
    importStorageInventoryCSVDesktop()
  } else {
    const input = document.createElement('input')
    input.type = 'file'; input.accept = '.csv'
    input.onchange = e => { const f = e.target.files[0]; if (f) f.text().then(parseStorageInventoryCSVText) }
    input.click()
  }
}
async function importStorageInventoryCSVDesktop() {
  const fp = await window.api.pickFile({ title:'Import Supply Location Inventory CSV', filters:[{name:'CSV',extensions:['csv']}] })
  if (!fp) return
  const res = await window.api.readFile(fp)
  if (res?.data) parseStorageInventoryCSVText(res.data)
}
function parseStorageInventoryCSVText(text) {
  const lines = text.trim().split(/\r?\n/)
  if (lines.length < 2) { showToast('CSV appears empty'); return }
  const header = parseCSVLine(lines[0]).map(h => h.trim().toLowerCase())
  const col = (...names) => header.findIndex(h => names.some(n => h.includes(n)))
  const idx = {
    loc:  col('location','storage'),
    supply: col('supply','item','name'),
    sku:  col('sku','barcode'),
    qty:  col('quantity','qty'),
  }
  if (idx.supply < 0 || idx.loc < 0) { showToast('CSV needs "Storage Location" and "Supply Name" columns'); return }

  // Find or create a storage location by name, return its id
  const findOrCreateLocation = (name) => {
    name = (name||'').trim()
    if (!name) return ''
    let loc = S.storageLocations.find(l => l.name.toLowerCase() === name.toLowerCase())
    if (!loc) { loc = { id:'loc_'+uid(), name }; S.storageLocations.push(loc) }
    return loc.id
  }

  let updated = 0, skippedUnknown = 0, skippedBad = 0
  const skippedNames = new Set()
  const skippedRows = []  // full rows that didn't import, with a reason
  const headerCols = parseCSVLine(lines[0])
  for (let i = 1; i < lines.length; i++) {
    if (!lines[i].trim()) continue
    const cols = parseCSVLine(lines[i])
    const locName = cols[idx.loc]?.trim() || ''
    const supplyName = cols[idx.supply]?.trim() || ''
    const barcode = idx.sku>=0 ? (cols[idx.sku]?.trim()||'') : ''
    const qty = idx.qty>=0 ? +(cols[idx.qty]?.trim()||0) : 0
    if (!supplyName) { skippedBad++; skippedRows.push({ loc:locName, pep:supplyName, sku:barcode, qty:cols[idx.qty]?.trim()||'', reason:'Missing supply name' }); continue }

    const supply = (S.injectionSupplies||[]).find(x => x.name.toLowerCase() === supplyName.toLowerCase())
    if (!supply) { skippedUnknown++; skippedNames.add(supplyName); skippedRows.push({ loc:locName, pep:supplyName, sku:barcode, qty:cols[idx.qty]?.trim()||'', reason:'Supply not found in inventory' }); continue }

    const locId = findOrCreateLocation(locName)
    if (!supply.skus) supply.skus = []
    let sku = barcode ? supply.skus.find(s => (s.barcode||'') === barcode || (s.lotNumber||'') === barcode) : null
    if (!sku && supply.skus.length === 1) sku = supply.skus[0]
    if (!sku) {
      sku = {
        id: 'is_'+uid(), barcode, vendor:'', lotNumber:'', sizeGauge:'',
        expiryDate:'', costPerUnit:'', reorderUrl:'', locations: [], transactions: []
      }
      supply.skus.push(sku)
    }
    if (!sku.locations) sku.locations = []

    // Quantity = units in THIS container/location only. This sets the per-location
    // placement and does NOT change the SKU's overall stock total (no transaction).
    // Re-importing the same location overwrites its qty (so re-runs don't double up).
    if (locId && qty > 0) {
      const existingLoc = sku.locations.find(l => l.locationId === locId)
      if (existingLoc) existingLoc.qty = qty
      else sku.locations.push({ locationId: locId, qty })
    }
    supply.updatedAt = now()
    updated++
  }

  if (updated) logChange('supply', null, 'updated', `Supply location import: updated ${updated} row(s)`)
  save(); render()
  let msg = `Imported ${updated} row${updated!==1?'s':''}`
  if (skippedUnknown || skippedBad) msg += ` · ${skippedUnknown+skippedBad} skipped`
  showToast(msg)

  // If anything was skipped, stash it and offer a download of the unmatched rows
  if (skippedRows.length) {
    S._lastStorageSkips = skippedRows
    openModal('storageImportSkips')
  }
}

// Download the skipped/unmatched rows from the last storage import as a CSV (re-importable after fixing)
// ── Import transaction cleanup ────────────────────────
// ── Storage size breakdown ────────────────────────────
// Measures the JSON byte size of each data array so you can see what's bloating sync.
function storageSizeBreakdown() {
  const keys = ['peptides','documents','storageLocations','injections','healthData','notes','labResults','supplements','users','peptideLibrary','changeLog','customCategories','refillLog','injectionSupplies','peptidePens','vendors','cycleTimelines','blends']
  const rows = []
  let maxChunk = 0
  for (const k of keys) {
    const arr = S[k] || []
    const bytes = JSON.stringify(arr).length
    const count = Array.isArray(arr) ? arr.length : 0
    // For chunked keys, calculate the largest year-chunk
    if (CHUNKED_KEYS.includes(k)) {
      const byYear = {}
      for (const r of arr) {
        const y = (r.date || r.dateTime || '').substring(0, 4)
        if (/^\d{4}$/.test(y)) (byYear[y] = byYear[y] || []).push(r)
      }
      const chunkSizes = Object.values(byYear).map(c => JSON.stringify(c).length)
      const biggest = Math.max(0, ...chunkSizes)
      maxChunk = Math.max(maxChunk, biggest)
      rows.push({ key: k + ' (chunked by year)', bytes, count, biggestChunk: biggest })
    } else {
      maxChunk = Math.max(maxChunk, bytes)
      rows.push({ key: k, bytes, count, biggestChunk: bytes })
    }
  }
  rows.sort((a,b) => b.bytes - a.bytes)
  const total = rows.reduce((a,r) => a + r.bytes, 0)
  return { rows, total, maxChunk }
}

function countImportTransactions() {
  let importTx = 0, vials = 0
  for (const p of (S.peptides||[])) {
    for (const s of (p.skus||[])) {
      for (const t of (s.transactions||[])) {
        if (typeof t.notes === 'string' && t.notes.startsWith('Imported into ')) {
          importTx++; vials += (+t.qty || 0)
        }
      }
    }
  }
  return { importTx, vials }
}

function countDuplicates(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return 0
  const seen = new Set()
  let dups = 0
  for (const r of arr) {
    // Use content fingerprint (date+type+value) for health/injection data,
    // since sync bugs create new IDs for each duplicate
    const fp = (r.date||'') + '|' + (r.type||r.peptide||r.name||'') + '|' + (r.value||r.dosage||'') + '|' + (r.unit||'')
    if (seen.has(fp)) dups++
    else seen.add(fp)
  }
  return dups
}

async function dedupArray(key) {
  const arr = S[key]
  if (!Array.isArray(arr)) return
  const before = arr.length
  const seen = new Set()
  const deduped = []
  for (const r of arr) {
    const fp = (r.date||'') + '|' + (r.type||r.peptide||r.name||'') + '|' + (r.value||r.dosage||'') + '|' + (r.unit||'')
    if (!seen.has(fp)) { seen.add(fp); deduped.push(r) }
  }
  S[key] = deduped
  const removed = before - deduped.length
  await save()
  showToast(`Removed ${removed} duplicate ${key} records (${before} → ${deduped.length})`)
}

function openImportCleanup() {
  const { importTx } = countImportTransactions()
  if (!importTx) { showToast('No import transactions to clean up'); return }
  openModal('importCleanup')
}

function runImportCleanup() {
  let removed = 0
  for (const p of (S.peptides||[])) {
    for (const s of (p.skus||[])) {
      if (!s.transactions) continue
      const before = s.transactions.length
      s.transactions = s.transactions.filter(t =>
        !(typeof t.notes === 'string' && t.notes.startsWith('Imported into ')))
      removed += before - s.transactions.length
    }
    p.updatedAt = now()
  }
  logChange('peptide', null, 'updated', `Cleaned up ${removed} import transaction(s)`)
  save(); closeModal(); render()
  showToast(`Removed ${removed} import transaction${removed!==1?'s':''}`)
}

// One-time cleanup: remove transactions created by the old storage import
// (notes starting with "Imported into ") and optionally their offsetting bulk "out"s.
function cleanupImportTransactions(alsoRemoveOffsets) {
  let removedImports = 0, removedOffsets = 0, skusTouched = 0
  for (const p of (S.peptides||[])) {
    for (const s of (p.skus||[])) {
      if (!s.transactions?.length) continue
      const before = s.transactions.length
      // Drop any "in" transaction whose note marks it as an import placement
      s.transactions = s.transactions.filter(t => {
        const isImport = t.type === 'in' && /^Imported into /i.test(t.notes || '')
        if (isImport) removedImports++
        return !isImport
      })
      if (alsoRemoveOffsets) {
        // Remove note-less bulk "out" transactions dated on the import day (2026-06-26)
        // that were added to offset the triple-import. These have empty notes and large qty.
        s.transactions = s.transactions.filter(t => {
          const isOffset = t.type === 'out' && (!t.notes || t.notes === '') &&
                           String(t.date||'').startsWith('2026-06-26') && (+t.qty >= 10)
          if (isOffset) removedOffsets++
          return !isOffset
        })
      }
      if (s.transactions.length !== before) skusTouched++
    }
  }
  logChange('peptide', null, 'updated', `Cleaned up import transactions (removed ${removedImports} imports${alsoRemoveOffsets?`, ${removedOffsets} offsets`:''})`)
  save(); render()
  showToast(`Removed ${removedImports} import + ${removedOffsets} offset tx across ${skusTouched} SKUs`)
  return { removedImports, removedOffsets, skusTouched }
}

function clearLocationInventory() {
  // Clear supply placements; peptide placement is held in vaultAssignments.
  let cleared = 0
  for (const supply of (S.injectionSupplies||[])) {
    for (const s of (supply.skus||[])) {
      if (s.locations?.length) { cleared += s.locations.length; s.locations = [] }
    }
  }
  logChange('location', null, 'updated', `Cleared location inventory (${cleared} placement${cleared!==1?'s':''})`)
  save(); closeModal(); render()
  showToast('Location inventory cleared')
}

function deleteAllLocations() {
  // Remove supply-location definitions and supply placements only.
  const count = S.storageLocations.length
  for (const supply of (S.injectionSupplies||[])) {
    for (const s of (supply.skus||[])) {
      if (s.locations?.length) s.locations = []
    }
  }
  S.storageLocations = []
  logChange('location', null, 'deleted', `Deleted all ${count} location(s) and their inventory`)
  save(); closeModal(); render()
  showToast(`Deleted ${count} location${count!==1?'s':''}`)
}

function downloadStorageSkips() {
  const rows = S._lastStorageSkips || []
  if (!rows.length) { showToast('No skipped rows'); return }
  const data = [['Storage Location','Supply Name','SKU (barcode)','Quantity','Reason Skipped']]
  for (const r of rows) data.push([r.loc, r.pep, r.sku, r.qty, r.reason])
  const csv = data.map(row => row.map(c => `"${String(c??'').replace(/"/g,'""')}"`).join(',')).join('\n')
  const filename = 'Supply_Location_Import_Unmatched.csv'
  if (window.api) {
    window.api.showSaveDialog({ title:'Save Unmatched Rows', defaultPath:filename, filters:[{name:'CSV',extensions:['csv']}] })
      .then(p => { if (p) window.api.writeFile(p, csv).then(()=>showToast('Saved unmatched rows')) })
  } else {
    const blob = new Blob([csv], { type:'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = filename; a.click()
    setTimeout(()=>URL.revokeObjectURL(url), 1000)
    showToast('Downloaded unmatched rows')
  }
}

// ── CSV Export ────────────────────────────────────────
async function exportInjectionCSV() {
  if (!S.injections.length) { showToast('No injections to export'); return }
  const userFilter = S.calUserFilter || 'all'
  const rows = [['Date','Time','Peptide','Dosage','Draw Units','User','Notes']]
  let sorted = [...S.injections].sort((a,b) => a.date.localeCompare(b.date) || injTimeMinutes(a.time) - injTimeMinutes(b.time))
  if (userFilter !== 'all') sorted = sorted.filter(inj => (inj.userId||'') === userFilter)
  for (const inj of sorted) {
    const dispDate = new Date(inj.date + 'T12:00:00').toLocaleDateString('en-US')
    const userName = (S.users||[]).find(u => u.id === inj.userId)?.name || ''
    rows.push([dispDate, fmt12(inj.time), inj.peptideName, inj.dosage, inj.drawUnits, userName, inj.notes||''])
  }
  const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n')
  const userTag = userFilter !== 'all' ? `_${(S.users||[]).find(u=>u.id===userFilter)?.name||''}` : ''
  const filename = `PeptideVault_Injections${userTag}_${localDateKey()}.csv`
  if (window.api) {
    const sp = await window.api.showSaveDialog({ title:'Export Injections', defaultPath:filename, filters:[{name:'CSV',extensions:['csv']}] })
    if (sp) { await window.api.writeFile(sp, csv); showToast('Injections exported') }
  } else {
    const a = document.createElement('a')
    a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
    a.download = filename; a.click()
    showToast('Injections downloaded')
  }
}

async function clearInjections() {
  if (!await asyncConfirm('Delete all injection records?\n\nThis cannot be undone.')) return
  S.injections = []
  save(); render(); showToast('Injections cleared')
}

function assignUnassignedInjections() {
  const userId = document.getElementById('assign-user-select')?.value
  if (!userId) return
  const userName = (S.users||[]).find(u => u.id === userId)?.name || 'user'
  let count = 0
  for (const inj of S.injections) {
    if (!inj.userId) { inj.userId = userId; count++ }
  }
  logChange('injection', null, 'updated', `Assigned ${count} injection(s) to ${userName}`)
  save(); closeModal(); render()
  showToast(`Assigned ${count} injection${count!==1?'s':''} to ${userName}`)
}

// ── iCalendar (.ics) Export ───────────────────────────
function timeStrTo24(timeStr) {
  if (!timeStr) return '070000'
  const t = String(timeStr).trim()
  // Bare period labels (from imports): AM → 07:00, PM → 19:00
  if (/^am$/i.test(t)) return '070000'
  if (/^pm$/i.test(t)) return '190000'
  // Already 24hr: "08:00" or "08:00:00"
  const m24 = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
  if (m24) return m24[1].padStart(2,'0') + m24[2] + '00'
  // 12hr: "8:00 AM" or "8:00 PM"
  const m12 = t.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i)
  if (m12) {
    let h = parseInt(m12[1])
    const min = m12[2]
    const period = m12[3].toUpperCase()
    if (period === 'AM' && h === 12) h = 0
    if (period === 'PM' && h !== 12) h += 12
    return String(h).padStart(2,'0') + min + '00'
  }
  return '070000'
}

function icsEscape(str) {
  return String(str||'').replace(/\\/g,'\\\\').replace(/;/g,'\\;').replace(/,/g,'\\,').replace(/\n/g,'\\n')
}

// Count injections within a date window (inclusive)
function countInjectionsInRange(start, end, userId) {
  return S.injections.filter(inj =>
    (!start || inj.date >= start) &&
    (!end || inj.date <= end) &&
    (!userId || userId === 'all' || (inj.userId||'') === userId)
  ).length
}

function setICSPreset(days) {
  const today = new Date()
  const end = new Date(); end.setDate(end.getDate() + days)
  const s = localDateStr(today)
  const e = localDateStr(end)
  S.icsRangeStart = s; S.icsRangeEnd = e
  const si = document.getElementById('ics-range-start'); if (si) si.value = s
  const ei = document.getElementById('ics-range-end'); if (ei) ei.value = e
  updateICSRangeCount()
}

function openICSRangeModal() {
  if (!S.injections.length) { showToast('No injections to export'); return }
  // Default window: today → 90 days out
  const today = new Date()
  const end = new Date(); end.setDate(end.getDate() + 90)
  S.icsRangeStart = localDateStr(today)
  S.icsRangeEnd = localDateStr(end)
  // Default the export user to the current calendar filter
  S.icsUserFilter = S.calUserFilter || 'all'
  openModal('icsRange')
}

function updateICSRangeCount() {
  const s = document.getElementById('ics-range-start')?.value || ''
  const e = document.getElementById('ics-range-end')?.value || ''
  const u = document.getElementById('ics-user-filter')?.value || 'all'
  S.icsRangeStart = s; S.icsRangeEnd = e; S.icsUserFilter = u
  const n = countInjectionsInRange(s, e, u)
  const out = document.getElementById('ics-range-count')
  if (out) {
    const tooMany = n > 1000
    out.innerHTML = `<strong style="color:${tooMany?'var(--red)':'var(--green)'}">${n}</strong> event${n!==1?'s':''} in range`
      + (tooMany ? ' <span style="color:var(--red)">— Google may reject more than ~1,000. Narrow the range.</span>' : '')
  }
}

function confirmICSExport() {
  const s = document.getElementById('ics-range-start')?.value || ''
  const e = document.getElementById('ics-range-end')?.value || ''
  const u = document.getElementById('ics-user-filter')?.value || 'all'
  closeModal()
  exportInjectionICS(s, e, u)
}

async function exportInjectionICS(rangeStart, rangeEnd, userId) {
  if (!S.injections.length) { showToast('No injections to export'); return }

  let sorted = [...S.injections].sort((a,b) => a.date.localeCompare(b.date) || injTimeMinutes(a.time) - injTimeMinutes(b.time))
  // Filter to the requested window (inclusive). Dates are YYYY-MM-DD strings, so lexical compare works.
  if (rangeStart) sorted = sorted.filter(inj => inj.date >= rangeStart)
  if (rangeEnd)   sorted = sorted.filter(inj => inj.date <= rangeEnd)
  // Filter by user if specified
  if (userId && userId !== 'all') sorted = sorted.filter(inj => (inj.userId||'') === userId)

  if (!sorted.length) { showToast('No injections in that date range'); return }
  // DTSTAMP must be UTC in basic format: YYYYMMDDTHHMMSSZ
  const stamp = new Date().toISOString().replace(/[-:]/g,'').replace(/\.\d{3}/,'')

  const events = sorted.map((inj, i) => {
    const datePart = inj.date.replace(/-/g, '')
    const timePart = timeStrTo24(inj.time)
    const dtStart  = `${datePart}T${timePart}`
    const summary  = icsEscape(`${inj.peptideName} ${inj.dosage}`.trim()) || 'Injection'
    const descParts = []
    if (inj.drawUnits) descParts.push(`Draw Units: ${inj.drawUnits}`)
    if (inj.dosage) descParts.push(`Dose: ${inj.dosage}`)
    const desc = icsEscape(descParts.join('  '))
    // Unique, stable UID per event (date+index keeps it deterministic)
    const uid = `pv-${datePart}-${timePart}-${i}@peptidevault`
    return [
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${stamp}`,
      `DTSTART;TZID=America/Chicago:${dtStart}`,
      `DURATION:PT15M`,
      `SUMMARY:${summary}`,
      desc ? `DESCRIPTION:${desc}` : '',
      'STATUS:CONFIRMED',
      'TRANSP:TRANSPARENT',
      'END:VEVENT'
    ].filter(Boolean).join('\r\n')
  }).join('\r\n')

  // VTIMEZONE block so apps resolve America/Chicago correctly
  const vtimezone = [
    'BEGIN:VTIMEZONE',
    'TZID:America/Chicago',
    'BEGIN:DAYLIGHT',
    'TZOFFSETFROM:-0600',
    'TZOFFSETTO:-0500',
    'TZNAME:CDT',
    'DTSTART:19700308T020000',
    'RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=2SU',
    'END:DAYLIGHT',
    'BEGIN:STANDARD',
    'TZOFFSETFROM:-0500',
    'TZOFFSETTO:-0600',
    'TZNAME:CST',
    'DTSTART:19701101T020000',
    'RRULE:FREQ=YEARLY;BYMONTH=11;BYDAY=1SU',
    'END:STANDARD',
    'END:VTIMEZONE'
  ].join('\r\n')

  const ics = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//PeptideVault//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'X-WR-CALNAME:PeptideVault Injections',
    'X-WR-TIMEZONE:America/Chicago',
    vtimezone,
    events,
    'END:VCALENDAR'
  ].join('\r\n') + '\r\n'

  const rangeTag = (rangeStart || rangeEnd) ? `_${rangeStart||'start'}_to_${rangeEnd||'end'}` : ''
  const userTag = (userId && userId !== 'all') ? `_${(S.users||[]).find(u=>u.id===userId)?.name||userId}` : ''
  const filename = `PeptideVault_Injections${userTag}${rangeTag}.ics`
  if (window.api) {
    const sp = await window.api.showSaveDialog({
      title: 'Export iCalendar',
      defaultPath: filename,
      filters: [{ name: 'iCalendar', extensions: ['ics'] }]
    })
    if (sp) { await window.api.writeFile(sp, ics); showToast('ICS exported — import via Google Calendar settings') }
  } else {
    const blob = new Blob([ics], { type:'text/calendar;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = filename; a.click()
    setTimeout(()=>URL.revokeObjectURL(url), 1000)
    showToast('ICS downloaded — import via Google Calendar settings')
  }
}

export {
  MONTHS,
  DAYS,
  CAL_COLORS,
  peptideColor,
  fmt12,
  injTimeMinutes,
  injectionsForDate,
  injectionsForPeptide,
  dateKey,
  renderCalendarTab,
  calPrev,
  calNext,
  showDayDetail,
  importInjectionCSV,
  parseInjectionCSV,
  parseInjectionCSVText,
  parseDate,
  parseCSVLine,
  importPeptideCSV,
  importPeptideCSVDesktop,
  parsePeptideCSVText,
  importLocationCSV,
  importLocationCSVDesktop,
  parseLocationCSVText,
  importStorageInventoryCSV,
  importStorageInventoryCSVDesktop,
  parseStorageInventoryCSVText,
  storageSizeBreakdown,
  countImportTransactions,
  countDuplicates,
  dedupArray,
  openImportCleanup,
  runImportCleanup,
  cleanupImportTransactions,
  clearLocationInventory,
  deleteAllLocations,
  downloadStorageSkips,
  exportInjectionCSV,
  clearInjections,
  assignUnassignedInjections,
  timeStrTo24,
  icsEscape,
  countInjectionsInRange,
  setICSPreset,
  openICSRangeModal,
  updateICSRangeCount,
  confirmICSExport,
  exportInjectionICS
}
